import EnvironmentalChargingOptionsCard from '@/authenticated/components/ChargeSchedule/EnvironmentalChargingOptionsCard'
import { EnergySystem } from '@/types/energySystem'
import Text from '@/components/Text'
import EmptyMailbox from '@/app/img/empty-mailbox.svg'
import { Card, LoadingAnimation } from '@/components'
import { LoadingContainer } from '@/components/Animations/FadeContainer/FadeContainer'
import { match } from 'ts-pattern'
import { useViewModel } from './useProgramTabViewModel'

type Props = {
  energySystem: EnergySystem
}

function LoadedEnergySystemProgramTab(props: ReturnType<typeof useViewModel>) {
  const {
    energySystemHasEnrolledInEligibleProgram,
    programsEnergySystemIsEligibleFor,
    userHasEnrolledInProgramButNotEnergySystem,
  } = props

  if (programsEnergySystemIsEligibleFor.length === 0) {
    return (
      <div className="flex flex-col gap-2 p-4 text-center bg-[#FBF6FF] rounded-2xl">
        <img src={EmptyMailbox} className="size-36 mx-auto" />
        <Text variant="h3">No Available Programs</Text>
        <Text variant="body2" className="text-base-grey-900">
          Come back soon as we're always looking for new programs for this
          device!
        </Text>
      </div>
    )
  }

  const enrollmentComponent = match({
    energySystemHasEnrolledInEligibleProgram,
    userHasEnrolledInProgramButNotEnergySystem,
    hasEligiblePrograms: programsEnergySystemIsEligibleFor.length > 0,
  })
    .with({ energySystemHasEnrolledInEligibleProgram: true }, () => (
      // If the energy system has enrolled in a program,
      // we should show the toggle that allows the user to control the enrollment
      <EnvironmentalChargingOptionsCard.EnrollmentToggleHeader
        energySystemId={String(props.energySystem.id)}
      />
    ))
    .with({ userHasEnrolledInProgramButNotEnergySystem: true }, () => (
      // If the user has enrolled at the profile level, but they did not enroll
      // this energy system, we should show the toggle, which creates the enrollment
      // when turned on.
      <EnvironmentalChargingOptionsCard.EnrollmentToggleHeader
        energySystemId={String(props.energySystem.id)}
      />
    ))
    .with({ hasEligiblePrograms: true }, () => (
      <>
        <EnvironmentalChargingOptionsCard.PlainHeader />
        <EnvironmentalChargingOptionsCard.ProgramEnrollmentUpsell
          energySystemId={String(props.energySystem.id)}
          buttonVariant="primary"
        />
      </>
    ))
    .otherwise(() => <EnvironmentalChargingOptionsCard.PlainHeader />)

  return (
    <section className={'flex flex-col w-full gap-4'}>
      {/* TODO: Adapt energy systems to use the new multi-program functionality */}
      {/* <DeviceProgramCards /> */}
      <div id="devices-energy-system-tab" className="flex flex-col w-full">
        <EnvironmentalChargingOptionsCard>
          {enrollmentComponent}
        </EnvironmentalChargingOptionsCard>
      </div>
    </section>
  )
}

export default function EnergySystemDevicesProgramTab(props: Props) {
  const viewModel = useViewModel(props)

  const loadingContent = (
    <Card>
      <div className="flex flex-col gap-4">
        <Text variant="h3">Program Enrollments</Text>
        <Text variant="body2">Looking up the programs for this device...</Text>
        <LoadingAnimation type="falling" centered />
      </div>
    </Card>
  )

  const loadedContent = <LoadedEnergySystemProgramTab {...viewModel} />

  return (
    <section className={'flex flex-col w-full gap-4'}>
      {/* TODO: Adapt energy systems to use the new multi-program functionality */}
      {/* <DeviceProgramCards /> */}
      <div id="devices-energy-system-tab" className="flex flex-col w-full">
        <LoadingContainer
          isLoading={viewModel.isLoading}
          loadedContent={loadedContent}
          loadingContent={loadingContent}
        />
      </div>
    </section>
  )
}
